import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Coverage, Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject, ReplaySubject, combineLatest } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { PolicyService } from '../../../policy';
import { WarrantyWebService } from '../../../warranty/services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-coverage-carousel',
  templateUrl: './coverage-carousel.component.html',
  styleUrls: ['coverage-carousel.component.scss'],
  standalone: false,
})
export class CoverageCarouselComponent {
  constructor(
    private policySvc: PolicyService,
    private warrantySvc: WarrantyWebService
  ) {}

  @PropertyListener('warranty') warranty$ = new ReplaySubject<Warranty.Model>();
  @Input() warranty?: Warranty.Model;

  @Input() isCustomerPortal = false;

  @PropertyListener('selectedGroup') selectedGroup$ = new BehaviorSubject<string | null>(null);
  selectedGroup: Coverage.Model['group'] | null = null;

  readonly pageSize = 8;
  readonly pageIndex$ = new BehaviorSubject(0);

  readonly showDisabledCoverages$ = new BehaviorSubject(false);

  readonly hasDisabledCoverages$ = this.warranty$.pipe(
    map(warranty => warranty?.coverages?.some(coverage => !Warranty.isCoverageIncluded(coverage)) ?? false)
  );

  readonly evaluatedLiabilityGroups$ = this.warranty$.pipe(
    switchMap(async warranty => {
      if (!warranty) return null;

      return await this.policySvc.getPlanLiabilityGroups(
        warranty.policyId,
        warranty.planId,
        warranty.propertySetSubmissionId
      );
    })
  );

  readonly allCoverages$ = combineLatest([
    this.warranty$,
    this.showDisabledCoverages$.pipe(tap(() => (this.selectedGroup = null))),
    this.hasDisabledCoverages$,
  ]).pipe(
    map(([warranty, showDisabledCoverages, hasDisabledCoverages]) => {
      if (!warranty?.coverages) return [];

      return warranty?.coverages
        ?.filter(coverage =>
          hasDisabledCoverages
            ? showDisabledCoverages
              ? !Warranty.isCoverageIncluded(coverage)
              : Warranty.isCoverageIncluded(coverage)
            : true
        )
        .map(cvg => {
          return {
            ...cvg,
            coverage: this.warranty?.policy?.coverages?.find(cov => cov.id === cvg.coverageId),
          };
        });
    })
  );

  readonly filteredCoverages$ = combineLatest([this.allCoverages$, this.selectedGroup$, this.warranty$]).pipe(
    switchMap(async ([coverages, selectedGroup, warranty]) => {
      const filteredCoverages = coverages.filter(coverage =>
        selectedGroup ? coverage.coverage?.group === selectedGroup : true
      );

      const coverageIds = filteredCoverages.map(coverage => coverage.coverageId);

      const filteredCoveragesWithLiabilityInfo = await this.warrantySvc.calculateLiabilityLimit(
        warranty.id,
        coverageIds
      );
      const items = filteredCoverages.map(item => {
        const liabilityInfo =
          filteredCoveragesWithLiabilityInfo.find(i => i.coverageId === item.coverageId)?.liabilityInfo ?? null;

        return {
          ...item,
          liabilityInfo,
        };
      });

      return items;
    }),
    // Reset to the first page whenever the filtered list changes (group, excluded toggle, warranty)
    tap(() => this.pageIndex$.next(0)),
    // Shared so totalPages$ and pagedCoverages$ don't each re-run calculateLiabilityLimit
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly totalPages$ = this.filteredCoverages$.pipe(
    map(items => Math.max(1, Math.ceil((items?.length ?? 0) / this.pageSize)))
  );

  readonly pagedCoverages$ = combineLatest([this.filteredCoverages$, this.pageIndex$]).pipe(
    map(([items, pageIndex]) => {
      const start = pageIndex * this.pageSize;
      return items.slice(start, start + this.pageSize);
    })
  );

  setPage(index: number) {
    this.pageIndex$.next(Math.max(0, index));
  }

  pageArray(totalPages: number | null): number[] {
    return Array.from({ length: totalPages ?? 0 }, (_, i) => i);
  }
}
